import { useAuth } from '@/app/_auth/auth-hooks'
import { taskManagerApiPaths } from '@/hooks/api/_paths'
import { useApiRequest } from '@/lib/http-utils'
import { DefaultError, useMutation, type UseMutationOptions } from '@tanstack/react-query'

const SEND_CHAT_429_ERROR = 'TOO_MANY_SEND_CHAT_REQUESTS'

type SendMessageAPIRequestBody = {
  content: string
  role: 'user'
  name: string
  attachments?: { file_id: string; purpose: string[] }[]
  preference?: {
    review_plan: boolean
  }
  metadata: Record<string, any>
  response_format: Record<string, any>
}

type SendMessageAPIResponse = {
  workflowId: string
  turnId: string
  title: string
  moderation: {
    appropriateness: string
    policies: string[]
    categories: {
      name: string
      score: number
    }[]
    emergence_guardrails: boolean
    native_model_safety_trigger: boolean
  }
}

type SendMessageVariables = {
  workflowId: string
  prompt: string
  files: FileList | null
}

function useSendMessageMutation<TContext = unknown>(
  options: UseMutationOptions<
    SendMessageAPIResponse,
    DefaultError,
    SendMessageVariables,
    TContext
  > = {},
) {
  const { user } = useAuth()
  const { apiRequest, domainId } = useApiRequest()
  return useMutation({
    mutationKey: ['send-chat-message'],
    mutationFn: async ({ workflowId, prompt, files }) => {
      const formData = new FormData()
      formData.append('data', JSON.stringify(port(prompt, user?.fullName ?? '')))
      if (files && files.length > 0) {
        for (const file of files) {
          formData.append('files', file)
        }
      }
      const response = await apiRequest({
        path: taskManagerApiPaths.workflow({ workflowId, domainId }),
        type: 'MULTIPART',
        options: {
          method: 'POST',
          body: formData,
        },
      })

      if (!response.ok)
        throw new Error(
          response?.status === 429 ? SEND_CHAT_429_ERROR : 'FAILED_TO_SEND_CHAT_MESSAGE',
        )

      const json = (await response.json()) as SendMessageAPIResponse

      return json
    },
    ...options,
  })
}

const port = (content: string, name: string): SendMessageAPIRequestBody => ({
  content,
  role: 'user',
  name,
  metadata: {},
  response_format: {},
  attachments: [],
})

export { SEND_CHAT_429_ERROR, useSendMessageMutation }
